import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import type { BlogArticle } from "../../data/blog"
import apiClient from "../../api/apiClient"

interface BlogState {
  articles: BlogArticle[]
  isLoading: boolean
  error: string | null
}

const initialState: BlogState = {
  articles: [],
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchArticles = createAsyncThunk("blog/fetchArticles", async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get("/blog")
    // Ensure dates are properly formatted
    const articles = response.data.articles.map((article: any) => ({
      ...article,
      date: article.date || new Date().toISOString(),
    }))
    return articles
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.error || "Failed to fetch articles")
  }
})

export const getArticleById = createAsyncThunk(
  "blog/getArticleById",
  async (articleId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/blog/${articleId}`)
      const article = {
        ...response.data.article,
        date: response.data.article.date || new Date().toISOString(),
      }
      return article
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to get article")
    }
  },
)

export const createArticle = createAsyncThunk(
  "blog/createArticle",
  async (
    { title, preview, content, thumbnail }: { title: string; preview: string; content: string; thumbnail?: File },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData()
      formData.append("title", title)
      formData.append("preview", preview)
      formData.append("content", content)

      if (thumbnail) {
        formData.append("thumbnail", thumbnail)
      }

      const response = await apiClient.post("/blog", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      return {
        ...response.data.article,
        date: response.data.article.date || new Date().toISOString(),
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to create article")
    }
  },
)

export const updateArticle = createAsyncThunk(
  "blog/updateArticle",
  async (
    {
      id,
      title,
      preview,
      content,
      thumbnail,
    }: { id: string; title: string; preview: string; content: string; thumbnail?: File },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData()
      formData.append("title", title)
      formData.append("preview", preview)
      formData.append("content", content)

      if (thumbnail) {
        formData.append("thumbnail", thumbnail)
      }

      const response = await apiClient.put(`/blog/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      return {
        ...response.data.article,
        id,
        date: response.data.article?.date || new Date().toISOString(),
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to update article")
    }
  },
)

export const deleteArticle = createAsyncThunk("blog/deleteArticle", async (articleId: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/blog/${articleId}`)
    return articleId
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.error || "Failed to delete article")
  }
})

const blogSlice = createSlice({
  name: "blog",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch articles
      .addCase(fetchArticles.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchArticles.fulfilled, (state, action: PayloadAction<BlogArticle[]>) => {
        state.articles = action.payload
        state.isLoading = false
        state.error = null
      })
      .addCase(fetchArticles.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Get article by ID
      .addCase(getArticleById.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getArticleById.fulfilled, (state, action) => {
        const index = state.articles.findIndex((article) => article.id === action.payload.id)
        if (index !== -1) {
          state.articles[index] = action.payload
        } else {
          state.articles.push(action.payload)
        }
        state.isLoading = false
        state.error = null
      })
      .addCase(getArticleById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Create article
      .addCase(createArticle.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createArticle.fulfilled, (state, action) => {
        state.articles.unshift(action.payload)
        state.isLoading = false
        state.error = null
      })
      .addCase(createArticle.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Update article
      .addCase(updateArticle.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateArticle.fulfilled, (state, action) => {
        const index = state.articles.findIndex((article) => article.id === action.payload.id)
        if (index !== -1) {
          state.articles[index] = action.payload
        }
        state.isLoading = false
        state.error = null
      })
      .addCase(updateArticle.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Delete article
      .addCase(deleteArticle.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteArticle.fulfilled, (state, action) => {
        state.articles = state.articles.filter((article) => article.id !== action.payload)
        state.isLoading = false
        state.error = null
      })
      .addCase(deleteArticle.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export default blogSlice.reducer
